import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  User,
  Lock,
  CreditCard,
  Globe,
  Save,
  Loader2,
  Eye,
  EyeOff,
  Store,
  Calendar,
  Shield,
  CheckCircle,
  Send,
  Bell,
  Unlink,
  MessageCircle,
  ExternalLink,
  Info,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';

interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  avatar: string | null;
  role: string;
  language: string | null;
  timezone: string | null;
  createdAt: string;
}

interface UserSettings {
  theme: string;
  currency: string;
  emailNotifications: boolean;
  telegramNotifications: boolean;
  telegramChatId: string | null;
  businessName: string | null;
  businessAddress: string | null;
  businessPhone: string | null;
  businessEmail: string | null;
}

interface Subscription {
  id: string;
  plan: string;
  status: string;
  storesUsed: number;
  storesLimit: number;
  currentPeriodEnd: string;
}

// Helper to format date safely
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'preferences' | 'subscription'>('profile');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [telegramLinking, setTelegramLinking] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch profile
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => api.get<{ user: UserProfile }>('/users/profile'),
  });

  // Fetch settings
  const { data: settingsData } = useQuery({
    queryKey: ['user-settings'],
    queryFn: () => api.get<{ settings: UserSettings }>('/users/settings'),
  });

  // Fetch subscription history
  const { data: subscriptionData } = useQuery({
    queryKey: ['subscription-history'],
    queryFn: () => api.get<{ subscription: Subscription }>('/users/subscription-history'),
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<UserProfile>) => api.patch('/users/profile', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      toast.success('Profile updated');
    },
    onError: () => toast.error('Failed to update profile'),
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (data: Partial<UserSettings>) => api.patch('/users/settings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-settings'] });
      toast.success('Settings saved');
    },
    onError: () => toast.error('Failed to save settings'),
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
      api.post('/users/change-password', data),
    onSuccess: () => {
      toast.success('Password changed successfully');
      // Reset form
      const form = document.getElementById('password-form') as HTMLFormElement;
      form?.reset();
    },
    onError: (error: any) => {
      const message = error.data?.error || error.data?.details?._errors?.[0] || 'Failed to change password';
      toast.error(message);
    },
  });

  // Connect Telegram mutation - generates link and opens Telegram
  const connectTelegramMutation = useMutation({
    mutationFn: () => api.post<{ url: string; expiresIn: number }>('/users/telegram-link', {}),
    onSuccess: (data) => {
      // Open Telegram link in new tab
      window.open(data.url, '_blank');
      setTelegramLinking(true);
      toast.success('Telegram opened! Press Start in the bot to connect.');
      
      // Start polling for connection status
      pollingRef.current = setInterval(async () => {
        try {
          const status = await api.get<{ connected: boolean }>('/users/telegram-status');
          if (status.connected) {
            // Connected! Stop polling and refresh settings
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setTelegramLinking(false);
            queryClient.invalidateQueries({ queryKey: ['user-settings'] });
            toast.success('Telegram connected successfully!');
          }
        } catch {
          // Ignore polling errors
        }
      }, 2000);
      
      // Stop polling after expiry (10 min)
      setTimeout(() => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setTelegramLinking(false);
        }
      }, data.expiresIn * 1000);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to generate Telegram link');
    },
  });

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Disconnect Telegram mutation
  const disconnectTelegramMutation = useMutation({
    mutationFn: () => api.post('/users/disconnect-telegram', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-settings'] });
      toast.success('Telegram disconnected');
    },
    onError: () => toast.error('Failed to disconnect Telegram'),
  });

  const profile = profileData?.user;
  const settings = settingsData?.settings;
  const subscription = subscriptionData?.subscription;

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User, color: 'text-emerald-600' },
    { id: 'security', label: 'Security', icon: Lock, color: 'text-amber-600' },
    { id: 'preferences', label: 'Preferences', icon: Globe, color: 'text-sky-600' },
    { id: 'subscription', label: 'Subscription', icon: CreditCard, color: 'text-purple-600' },
  ];

  const handleProfileSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    updateProfileMutation.mutate({
      name: formData.get('name') as string,
      language: formData.get('language') as string,
      timezone: formData.get('timezone') as string,
    });
  };

  const handleSettingsSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    updateSettingsMutation.mutate({
      currency: formData.get('currency') as string,
      emailNotifications: formData.get('emailNotifications') === 'on',
      businessName: formData.get('businessName') as string || null,
      businessEmail: formData.get('businessEmail') as string || null,
      businessPhone: formData.get('businessPhone') as string || null,
    });
  };

  const handlePasswordSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    changePasswordMutation.mutate({
      currentPassword: formData.get('currentPassword') as string,
      newPassword: formData.get('newPassword') as string,
      confirmPassword: formData.get('confirmPassword') as string,
    });
  };

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-[#4A7C2E] animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account and preferences</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 p-1.5 rounded-xl bg-white border border-gray-200 shadow-sm w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all',
              activeTab === tab.id
                ? 'bg-[#4A7C2E] text-white shadow-md'
                : 'text-slate-600 hover:text-slate-900 hover:bg-gray-100'
            )}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
            <div className="p-2.5 rounded-xl bg-emerald-50">
              <User className="text-emerald-600" size={22} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Profile Information</h2>
              <p className="text-sm text-slate-500">Update your personal details</p>
            </div>
          </div>
          
          <form onSubmit={handleProfileSubmit} className="space-y-6">
            {/* Avatar Section */}
            <div className="flex items-center gap-5 p-4 bg-gradient-to-r from-gray-50 to-emerald-50/30 rounded-xl border border-gray-100">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A7C2E] to-emerald-600 flex items-center justify-center text-2xl font-bold text-white shadow-lg">
                {profile?.name?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1">
                <p className="text-slate-900 font-semibold text-lg">{profile?.name || 'No name set'}</p>
                <p className="text-slate-500 text-sm">{profile?.email}</p>
                <p className="text-slate-400 text-xs mt-1 flex items-center gap-1">
                  <Calendar size={12} />
                  Member since {formatDate(profile?.createdAt)}
                </p>
              </div>
              <div className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium capitalize">
                {profile?.role?.toLowerCase() || 'user'}
              </div>
            </div>

            {/* Form fields */}
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="label">Full Name</label>
                <input
                  type="text"
                  name="name"
                  defaultValue={profile?.name || ''}
                  placeholder="Enter your name"
                  className="input"
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={profile?.email || ''}
                  disabled
                  className="input bg-gray-50 text-slate-500 cursor-not-allowed"
                />
                <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
                  <Lock size={10} /> Email cannot be changed
                </p>
              </div>
              <div>
                <label className="label">Language</label>
                <select name="language" defaultValue={profile?.language || 'en'} className="input">
                  <option value="en">🇬🇧 English</option>
                  <option value="th">🇹🇭 ไทย (Thai)</option>
                  <option value="es">🇪🇸 Español</option>
                  <option value="ru">🇷🇺 Русский</option>
                </select>
              </div>
              <div>
                <label className="label">Timezone</label>
                <select name="timezone" defaultValue={profile?.timezone || 'UTC'} className="input">
                  <option value="UTC">🌍 UTC</option>
                  <option value="Asia/Bangkok">🇹🇭 Asia/Bangkok (ICT)</option>
                  <option value="Europe/Madrid">🇪🇸 Europe/Madrid (CET)</option>
                  <option value="America/New_York">🇺🇸 America/New_York (EST)</option>
                </select>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={updateProfileMutation.isPending}
                className="btn-primary"
              >
                {updateProfileMutation.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                Save Profile
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
            <div className="p-2.5 rounded-xl bg-amber-50">
              <Lock className="text-amber-600" size={22} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Change Password</h2>
              <p className="text-sm text-slate-500">Update your security credentials</p>
            </div>
          </div>

          <form id="password-form" onSubmit={handlePasswordSubmit} className="space-y-5 max-w-md">
            <div>
              <label className="label">Current Password</label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  name="currentPassword"
                  required
                  className="input pr-10"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="label">New Password</label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  name="newPassword"
                  required
                  minLength={8}
                  className="input pr-10"
                  placeholder="Enter new password (min 8 characters)"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="label">Confirm New Password</label>
              <input
                type="password"
                name="confirmPassword"
                required
                className="input"
                placeholder="Confirm new password"
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={changePasswordMutation.isPending}
                className="btn-primary"
              >
                {changePasswordMutation.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Shield size={16} />
                )}
                Update Password
              </button>
            </div>
          </form>

          {/* Security info */}
          <div className="mt-8 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-100">
            <h3 className="text-slate-800 font-semibold mb-3 flex items-center gap-2">
              <Shield size={16} className="text-amber-600" />
              Security Tips
            </h3>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle size={14} className="text-amber-500 mt-0.5 shrink-0" />
                Use a strong password with letters, numbers, and symbols
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={14} className="text-amber-500 mt-0.5 shrink-0" />
                Don't reuse passwords from other sites
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={14} className="text-amber-500 mt-0.5 shrink-0" />
                Change your password regularly for better security
              </li>
            </ul>
          </div>
        </motion.div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Telegram Notifications Card */}
          <div className="card">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
              <div className="p-2.5 rounded-xl bg-sky-50">
                <Send className="text-sky-500" size={22} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Telegram Notifications</h2>
                <p className="text-sm text-slate-500">Receive instant notifications in Telegram</p>
              </div>
            </div>

            {/* Info Box */}
            <div className="p-4 bg-gradient-to-r from-sky-50 to-blue-50 rounded-xl border border-sky-100 mb-6">
              <div className="flex gap-3">
                <Info size={20} className="text-sky-600 shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700">
                  <p className="font-medium text-slate-800 mb-1">What you'll receive:</p>
                  <ul className="space-y-1 text-slate-600">
                    <li className="flex items-center gap-2">
                      <Bell size={14} className="text-sky-500" />
                      New order notifications with customer details
                    </li>
                    <li className="flex items-center gap-2">
                      <MessageCircle size={14} className="text-sky-500" />
                      Order status updates and changes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={14} className="text-sky-500" />
                      Payment confirmations
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {settings?.telegramChatId ? (
              /* Connected State */
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle size={24} className="text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-emerald-800">Telegram Connected</p>
                    <p className="text-sm text-emerald-600">
                      Chat ID: <code className="bg-emerald-100 px-2 py-0.5 rounded text-xs">{settings.telegramChatId}</code>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => disconnectTelegramMutation.mutate()}
                    disabled={disconnectTelegramMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                  >
                    {disconnectTelegramMutation.isPending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Unlink size={16} />
                    )}
                    Disconnect
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings?.telegramNotifications ?? false}
                      onChange={(e) => {
                        updateSettingsMutation.mutate({ telegramNotifications: e.target.checked });
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-sky-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-500"></div>
                    <span className="ml-3 text-sm font-medium text-slate-700">Enable Telegram notifications</span>
                  </label>
                </div>
              </div>
            ) : (
              /* Not Connected State */
              <div className="space-y-5">
                {telegramLinking ? (
                  /* Waiting for connection */
                  <div className="p-6 bg-sky-50 rounded-xl border border-sky-200 text-center">
                    <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-4">
                      <Loader2 size={32} className="text-sky-600 animate-spin" />
                    </div>
                    <h4 className="font-semibold text-sky-800 text-lg mb-2">Waiting for connection...</h4>
                    <p className="text-sm text-sky-600 mb-4">
                      Press <strong>Start</strong> in the Telegram bot to complete the connection.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        if (pollingRef.current) {
                          clearInterval(pollingRef.current);
                          pollingRef.current = null;
                        }
                        setTelegramLinking(false);
                      }}
                      className="text-sm text-slate-500 hover:text-slate-700 underline"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  /* Connect Button */
                  <>
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <p className="text-sm text-slate-600">
                        Click the button below to connect your Telegram. You'll be redirected to our bot — just press <strong>Start</strong> and you're done!
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => connectTelegramMutation.mutate()}
                      disabled={connectTelegramMutation.isPending}
                      className="w-full py-4 px-6 bg-[#0088cc] hover:bg-[#0077b5] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-3 shadow-lg shadow-[#0088cc]/20"
                    >
                      {connectTelegramMutation.isPending ? (
                        <Loader2 size={20} className="animate-spin" />
                      ) : (
                        <Send size={20} />
                      )}
                      Connect Telegram
                      <ExternalLink size={16} className="opacity-70" />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Main Preferences Card */}
          <div className="card">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
              <div className="p-2.5 rounded-xl bg-emerald-50">
                <Globe className="text-emerald-600" size={22} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Preferences & Business</h2>
                <p className="text-sm text-slate-500">Configure display and business settings</p>
              </div>
            </div>

            <form onSubmit={handleSettingsSubmit} className="space-y-8">
              {/* Display Preferences Section */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <div className="w-1.5 h-5 bg-sky-500 rounded-full" />
                  Display Preferences
                </h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label">Currency</label>
                    <select name="currency" defaultValue={settings?.currency || 'EUR'} className="input">
                      <option value="EUR">€ EUR - Euro</option>
                      <option value="USD">$ USD - US Dollar</option>
                      <option value="THB">฿ THB - Thai Baht</option>
                      <option value="BTC">₿ BTC - Bitcoin</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="emailNotifications"
                        id="emailNotifications"
                        defaultChecked={settings?.emailNotifications ?? true}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#4A7C2E]/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#4A7C2E]"></div>
                      <span className="ml-3 text-sm font-medium text-slate-700">Email notifications</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Business Info Section */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <div className="w-1.5 h-5 bg-purple-500 rounded-full" />
                  Business Information
                </h3>
                <p className="text-sm text-slate-500 -mt-2">Used for invoices and labels</p>
                
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label">Business Name</label>
                    <input
                      type="text"
                      name="businessName"
                      defaultValue={settings?.businessName || ''}
                      placeholder="Your Company Ltd"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Business Email</label>
                    <input
                      type="email"
                      name="businessEmail"
                      defaultValue={settings?.businessEmail || ''}
                      placeholder="contact@company.com"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Business Phone</label>
                    <input
                      type="tel"
                      name="businessPhone"
                      defaultValue={settings?.businessPhone || ''}
                      placeholder="+66 XX XXX XXXX"
                      className="input"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={updateSettingsMutation.isPending}
                  className="btn-primary"
                >
                  {updateSettingsMutation.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  Save Preferences
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Subscription Tab */}
      {activeTab === 'subscription' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Current Plan */}
          <div className="card">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
              <div className="p-2.5 rounded-xl bg-purple-50">
                <CreditCard className="text-purple-600" size={22} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Current Subscription</h2>
                <p className="text-sm text-slate-500">Manage your plan and billing</p>
              </div>
            </div>

            {subscription ? (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold text-slate-900">{subscription.plan}</span>
                      <span className={clsx(
                        'px-2.5 py-1 text-xs font-semibold rounded-full',
                        subscription.status === 'ACTIVE' 
                          ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' 
                          : 'bg-amber-100 text-amber-700 border border-amber-300'
                      )}>
                        {subscription.status}
                      </span>
                    </div>
                    <p className="text-slate-600 text-sm mt-1.5 flex items-center gap-1.5">
                      <Calendar size={14} />
                      Renews on {formatDate(subscription.currentPeriodEnd)}
                    </p>
                  </div>
                  <a href="/pricing" className="btn-secondary">
                    Manage Plan
                  </a>
                </div>

                {/* Usage Cards */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-5 bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-2 text-slate-600 mb-3">
                      <Store size={18} className="text-purple-500" />
                      <span className="font-medium">Stores Used</span>
                    </div>
                    <div className="text-3xl font-bold text-slate-900">
                      {subscription.storesUsed || 0} 
                      <span className="text-slate-400 text-lg font-normal">
                        / {subscription.storesLimit === -1 ? '∞' : subscription.storesLimit || 1}
                      </span>
                    </div>
                    {subscription.storesLimit !== -1 && subscription.storesLimit && (
                      <div className="mt-3 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#4A7C2E] to-emerald-500 rounded-full transition-all"
                          style={{ 
                            width: `${Math.min(100, ((subscription.storesUsed || 0) / subscription.storesLimit) * 100)}%` 
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="p-5 bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-2 text-slate-600 mb-3">
                      <Calendar size={18} className="text-sky-500" />
                      <span className="font-medium">Days Remaining</span>
                    </div>
                    <div className="text-3xl font-bold text-slate-900">
                      {Math.max(0, Math.ceil((new Date(subscription.currentPeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} 
                      <span className="text-slate-400 text-lg font-normal">days</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <CreditCard size={28} className="text-gray-400" />
                </div>
                <p className="text-slate-600 mb-4">No active subscription</p>
                <a href="/pricing" className="btn-primary inline-flex">
                  View Plans
                </a>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}
